import { getRootType, getParentType } from "@/backend/lib/utils";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { ContainerTypes, CreateContainerRequest } from "@/types/containerTypes";
import { AddBlockButton } from "../blocks/AddBlockButton";
import { BasicButton } from "../buttons/BasicButton";
import { Container } from "@/backend/models/containers/container";
import { Block } from "@/backend/models/block";
import { useCanAddNestedSection } from "@/frontend/hooks/useCanAddNestedSection";
import { ProcedureInstanceBase } from "@/backend/models/procedureInstances/procedureInstanceBase";
import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { ImplementationFunction } from "@/backend/models/implementationFunction";
import { CallFunctionButton } from "../functionalInterfaces/CallFunctionButton";
import { ParameterForRepeatingStepCycleSelector } from "../parameters/ParameterForRepeatingStepCylceSelector";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { FusawarePermission } from "@/frontend/fusawareUser";


export function ContainerButtonRow(props: { parent: Container | Block | ProcedureInstanceBase | ImplementationFunction, position: number, hideStep?: boolean, isScaffold?: boolean }) {
  const { position, parent, hideStep, isScaffold } = props;
  const createContainer = useTreeStore((state) => state.createContainer);
  const canAddNestedSection = useCanAddNestedSection(parent);
  const user = useGlobalStore((state) => state.userClass);

  function onClickCreateContainer(containerType: ContainerTypes, rangeParameterId?: string) {
    const request: CreateContainerRequest = {
      container: {
        parentId: parent.id,
        position,
        containerType,
        isScaffold: isScaffold || false,
        rootId: parent instanceof Container ? parent.rootId : parent.id,
        rootType: getRootType(parent),
        parentType: getParentType(parent),
        rangeParameterId,
      },
    };
    void createContainer(request);
  }

  return (
    <div className="pt-2 d-flex gap-1">
      { !hideStep &&
        <>
          <BasicButton onClick={() => onClickCreateContainer(ContainerTypes.Step)} label={I18n.get('addStep')} />
          { isScaffold && <RepeatingStepButton onClick={(rangeParameterId: string) => onClickCreateContainer(ContainerTypes.RepeatingStep, rangeParameterId)} /> }
          { parent instanceof Container && user?.fusaHasPermission(FusawarePermission.ADD_INTERFACES_TO_TEMPLATES) && <CallFunctionButton parent={parent} position={position} /> }
        </>
      }
      { canAddNestedSection && <BasicButton onClick={() => onClickCreateContainer(ContainerTypes.Section)} label={I18n.get('addSection')} /> }
      <AddBlockButton parent={parent} position={position} isScaffold={isScaffold} />
    </div>
  );
}

function RepeatingStepButton(props: { onClick: (rangeParameterId: string) => void }) {
  const { onClick } = props;
  const procedure = useProcedureInstanceStore((state) => state.procedureInstance);
  const [showModal, setShowModal] = useState(false);
  const [rangeParameterId, setRangeParameterId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  if (!procedure) {
    return null;
  }

  const onRangeParameterChange = (value?: string) => {
    setRangeParameterId(value);
    setError(undefined);
  };

  const onSubmit = () => {
    if (!rangeParameterId) {
      setError(I18n.get('noRangeParameter'));
      return;
    }
    if (rangeParameterId) {
      onClick(rangeParameterId);
      setShowModal(false);
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
    setRangeParameterId(undefined);
    setError(undefined);
  };

  return (
    <>
      <BasicButton onClick={() => setShowModal(true)} label={I18n.get('addRepeatingStep')} />
      { showModal &&
      <Modal show={showModal} onHide={onCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{I18n.get('createRepeatingStep')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex flex-column gap-2'>
            <ParameterForRepeatingStepCycleSelector onUpdate={onRangeParameterChange} currentId={rangeParameterId || ''} />
            { error && <p className='text-danger'>{error}</p> }
          </div>
        </Modal.Body>
        <Modal.Footer>
          <BasicButton className='btn btn-secondary' label={I18n.get('cancel')} onClick={() => setShowModal(false)} />
          <Button onClick={onSubmit}>{I18n.get('create')}</Button>
        </Modal.Footer>
      </Modal>
      }
    </>
  );
}
